import React, { useEffect, useState } from "react";
import { Card, Grid, Button, Tab, Tabs } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { knowledgeActions } from "store/knowledge-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchAllDocuments,
  fetchAllTags,
  deleteDocument,
  deleteTag,
  downloadDocument,
} from "app/services/knowledge.service";
import { DocumentDataGrid } from "./DocumentDataGrid";
import { TagDataGrid } from "./TagDataGrid";
import AddDocumentDialog from "./AddDocumentDialog";
import AddTagDialog from "./AddTagDialog";
import { ConfirmDialog } from "../ConfirmDialog";

export const KnowledgeAdminView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editDocument, setEditDocument] = useState(null);
  const [editTag, setEditTag] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  const documents = useSelector((state) => state.knowledge.documents);
  const tags = useSelector((state) => state.knowledge.tags);

  const loadDocuments = () => {
    setIsLoading(true);
    fetchAllDocuments()
      .then((data) => dispatch(knowledgeActions.setDocuments(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  const loadTags = () => {
    fetchAllTags()
      .then((data) => dispatch(knowledgeActions.setTags(data)))
      .catch((error) => handleAxiosError(error));
  };

  useEffect(() => {
    loadDocuments();
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (item, type) => {
    setDeleteItem(item);
    setDeleteType(type);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteType === "document" && deleteItem) {
      await deleteDocument(deleteItem.id);
      loadDocuments();
    } else if (deleteType === "tag" && deleteItem) {
      await deleteTag(deleteItem.id);
      loadTags();
    }
    setDeleteItem(null);
    setDeleteType("");
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || `${doc.documentTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      handleAxiosError(error);
    }
  };

  const publishedDocs = documents.filter((d) => d.status === "PUBLISHED").length;
  const pendingDocs = documents.filter((d) => d.status === "PENDING_REVIEW").length;

  return (
    <MDBox px={5} py={10}>
      <Grid container spacing={10}>
        {/* Stats Cards */}
        <Grid container spacing={10} item xs={12}>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="info">Documents</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{documents.length}</MDTypography>
                <MDTypography variant="caption">Total Documents</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="success">Published</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{publishedDocs}</MDTypography>
                <MDTypography variant="caption">Live Documents</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="warning">Pending</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{pendingDocs}</MDTypography>
                <MDTypography variant="caption">Awaiting Review</MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ width: "160px" }}>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="secondary">Tags</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{tags.length}</MDTypography>
              <MDTypography variant="caption">Categories</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Main Content with Tabs */}
        <Grid item xs={12} width="100%">
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Documents" />
                <Tab label="Tags" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditDocument(null); setDocumentDialogOpen(true); }} variant="contained" color="success">
                    Upload Document
                  </Button>
                </MDBox>
                <DocumentDataGrid documents={documents} isLoading={isLoading} onEdit={(d) => { setEditDocument(d); setDocumentDialogOpen(true); }} onDelete={(d) => handleDelete(d, "document")} onDownload={handleDownload} />
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditTag(null); setTagDialogOpen(true); }} variant="contained" color="success">
                    Create Tag
                  </Button>
                </MDBox>
                <TagDataGrid tags={tags} isLoading={isLoading} onEdit={(t) => { setEditTag(t); setTagDialogOpen(true); }} onDelete={(t) => handleDelete(t, "tag")} />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>

      <AddDocumentDialog open={documentDialogOpen} setOpen={setDocumentDialogOpen} onSuccess={loadDocuments} editObject={editDocument} availableTags={tags} />
      <AddTagDialog open={tagDialogOpen} setOpen={setTagDialogOpen} onSuccess={loadTags} editObject={editTag} />
      <ConfirmDialog title={`Delete ${deleteType}?`} open={confirmOpen} setOpen={setConfirmOpen} onConfirm={confirmDelete} yesButtonLabel="Delete" noButtonLabel="Cancel">
        Are you sure you want to delete this {deleteType}?
      </ConfirmDialog>
    </MDBox>
  );
};

